import { type TrellisCredentials, credentialsAreJwt, credentialsAreSSO } from "utils/trellisIntegration";
import { captureException } from "@sentry/react";
import axios, { type CancelToken, isCancel } from "axios";
import configuration from "utils/config";
import type { LoginResponse } from "./responses";

/**
 * Create headers for the login request using Trellis credentials.
 * @param credentials The credentials provided by Trellis.
 * @returns Headers to log in using Trellis protocols.
 */
function makeHeaders(credentials: TrellisCredentials) {
    if (credentialsAreSSO(credentials)) {
        return {
            Authorization: "Bearer " + credentials.sso,
            "Trellis-Referer": document.referrer,
        };
    }
    if (credentialsAreJwt(credentials)) {
        return {
            Authorization: "Trellis " + credentials.jwt,
            "Trellis-Referer": document.referrer,
        };
    } else {
        return {
            AuthToken: credentials.authToken,
            RegistrationToken: credentials.registrationToken,
            "Trellis-Referer": document.referrer,
        };
    }
}

// NOTE: This is not part of the rest of the API because it cannot be subject to the authentication behavior we attach
// to the API client--this IS the authentication.
/**
 * Log in to the Payments API using credentials provided by Trellis.
 * @param credentials The credentials provided by Trellis.
 * @returns The login response received from the Payments API.
 */
export async function loginTrellis(credentials: TrellisCredentials, cancelToken?: CancelToken) {
    try {
        const url = credentialsAreSSO(credentials) ? "login/SSO" : "login/trellis";

        const result = await axios.post<LoginResponse>(url, undefined, {
            baseURL: configuration.apiUrl,
            headers: makeHeaders(credentials),
            cancelToken,
        });

        // If CloudFront messes with the response or something weird happens, we might get a successful response
        // containing bogus data, so validate it first.
        if (result.data.authToken) {
            return result.data;
        } else {
            throw new Error("Unable to process login result.");
        }
    } catch (err) {
        if (!isCancel(err)) {
            captureException(err);
        }

        return undefined;
    }
}
